/**
 * Permissions Hook
 * Provides utilities for checking user permissions and roles
 */

import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Permission constants
 */
export const PERMISSIONS = {
  // Dashboard permissions
  DASHBOARD_VIEW: 'dashboard:view',

  // Vendor permissions
  VENDORS_VIEW: 'vendors:view',
  VENDORS_CREATE: 'vendors:create',
  VENDORS_EDIT: 'vendors:edit',
  VENDORS_DELETE: 'vendors:delete',

  // Customer permissions
  CUSTOMERS_VIEW: 'customers:view',
  CUSTOMERS_CREATE: 'customers:create',
  CUSTOMERS_EDIT: 'customers:edit',
  CUSTOMERS_DELETE: 'customers:delete',

  // Purchase permissions
  PURCHASES_VIEW: 'purchases:view',
  PURCHASES_CREATE: 'purchases:create',
  PURCHASES_EDIT: 'purchases:edit',
  PURCHASES_DELETE: 'purchases:delete',

  // Sales permissions
  SALES_VIEW: 'sales:view',
  SALES_CREATE: 'sales:create',
  SALES_EDIT: 'sales:edit',
  SALES_DELETE: 'sales:delete',

  // Expense permissions
  EXPENSES_VIEW: 'expenses:view',
  EXPENSES_CREATE: 'expenses:create',
  EXPENSES_EDIT: 'expenses:edit',
  EXPENSES_DELETE: 'expenses:delete',

  // Report permissions
  REPORTS_VIEW: 'reports:view',
  REPORTS_EXPORT: 'reports:export',

  // User Management permissions
  USER_VIEW: 'users:view',
  USER_CREATE: 'users:create',
  USER_EDIT: 'users:edit',
  USER_DELETE: 'users:delete',
  USER_ACTIVATE: 'users:activate',
  USER_DEACTIVATE: 'users:deactivate',

  // Settings permissions
  SETTINGS_VIEW: 'settings:view',
  SETTINGS_EDIT: 'settings:edit',
};

/**
 * Role constants
 */
export const ROLES = {
  ADMIN: 'ADMIN',
  ACCOUNTANT: 'ACCOUNTANT',
  STAFF: 'STAFF',
};

/**
 * Default permissions for each role
 */
export const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: [
    // All permissions
    ...Object.values(PERMISSIONS),
  ],
  [ROLES.ACCOUNTANT]: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.VENDORS_VIEW,
    PERMISSIONS.VENDORS_CREATE,
    PERMISSIONS.VENDORS_EDIT,
    PERMISSIONS.CUSTOMERS_VIEW,
    PERMISSIONS.CUSTOMERS_CREATE,
    PERMISSIONS.CUSTOMERS_EDIT,
    PERMISSIONS.PURCHASES_VIEW,
    PERMISSIONS.PURCHASES_CREATE,
    PERMISSIONS.PURCHASES_EDIT,
    PERMISSIONS.SALES_VIEW,
    PERMISSIONS.SALES_CREATE,
    PERMISSIONS.SALES_EDIT,
    PERMISSIONS.EXPENSES_VIEW,
    PERMISSIONS.EXPENSES_CREATE,
    PERMISSIONS.EXPENSES_EDIT,
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.REPORTS_EXPORT,
    PERMISSIONS.SETTINGS_VIEW,
  ],
  [ROLES.STAFF]: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.VENDORS_VIEW,
    PERMISSIONS.CUSTOMERS_VIEW,
    PERMISSIONS.PURCHASES_VIEW,
    PERMISSIONS.PURCHASES_CREATE,
    PERMISSIONS.SALES_VIEW,
    PERMISSIONS.SALES_CREATE,
    PERMISSIONS.EXPENSES_VIEW,
    PERMISSIONS.EXPENSES_CREATE,
    PERMISSIONS.REPORTS_VIEW,
  ],
};

/**
 * Hook for managing user permissions
 * @returns {Object} Permission utilities
 */
export const usePermissions = () => {
  const { user, hasPermission, hasRole } = useAuth();

  /**
   * Get all user permissions (explicit + role-based)
   */
  const userPermissions = useMemo(() => {
    if (!user) return [];

    // Backend returns enums like EXPENSE_READ; map to frontend string scheme
    const backendToFrontend = perm => {
      if (!perm) return null;
      const p = String(perm).toUpperCase();
      const mapSuffix = (module, suffix) => {
        const s = suffix.toLowerCase();
        return `${module}:${s === 'read' ? 'view' : s}`;
      };
      if (p.startsWith('EXPENSE_')) return mapSuffix('expenses', p.split('_')[1]);
      if (p.startsWith('PURCHASE_')) return mapSuffix('purchases', p.split('_')[1]);
      if (p.startsWith('SALE_')) return mapSuffix('sales', p.split('_')[1]);
      if (p.startsWith('VENDOR_')) return mapSuffix('vendors', p.split('_')[1]);
      if (p.startsWith('CUSTOMER_')) return mapSuffix('customers', p.split('_')[1]);
      if (p === 'REPORT_VIEW') return 'reports:view';
      if (p === 'ANALYTICS_VIEW') return 'reports:view';
      if (p === 'PROFIT_ANALYSIS_VIEW') return 'reports:view';
      if (p === 'FILE_UPLOAD') return 'files:upload';
      if (p === 'FILE_DOWNLOAD') return 'files:download';
      if (p.startsWith('MORTALITY_')) return `mortality:${p.split('_')[1].toLowerCase()}`;
      // User management maps
      if (p === 'USER_CREATE') return 'users:create';
      if (p === 'USER_READ') return 'users:view';
      if (p === 'USER_UPDATE') return 'users:edit';
      if (p === 'USER_DELETE') return 'users:delete';
      return null;
    };

    // Admins always have full access
    if (user.role === ROLES.ADMIN) {
      return [...new Set(Object.values(PERMISSIONS))];
    }

    const explicit = (user.permissions || [])
      .map(backendToFrontend)
      .filter(Boolean);
    const byRole = user.role ? (ROLE_PERMISSIONS[user.role] || []) : [];

    // If explicit overrides exist, use only explicit. Otherwise, fall back to role defaults
    let effective = explicit.length > 0 ? explicit : byRole;
    // Always allow dashboard view for authenticated users
    if (!effective.includes(PERMISSIONS.DASHBOARD_VIEW)) {
      effective = [...effective, PERMISSIONS.DASHBOARD_VIEW];
    }

    // Debug logging
    console.log('Permission Debug:', {
      userRole: user.role,
      explicitPermissions: explicit,
      rolePermissions: byRole,
      effectivePermissions: effective
    });

    return [...new Set(effective)];
  }, [user]);

  /**
   * Check if user can perform action
   */
  const can = permission => {
    if (!permission) return false;
    // Normalize to lowercase scheme used across UI
    const p = String(permission).toLowerCase();
    // First check explicit permissions
    const hasExplicit = hasPermission(p);
    if (hasExplicit) {
      console.log(`Permission ${p}: GRANTED (explicit)`);
      return true;
    }
    
    // Then check role-based permissions
    const hasRoleBased = userPermissions.includes(p);
    console.log(`Permission ${p}: ${hasRoleBased ? 'GRANTED' : 'DENIED'} (role-based)`, {
      userRole: user?.role,
      userPermissions,
      permission: p
    });
    
    return hasRoleBased;
  };

  /**
   * Check if user cannot perform action
   */
  const cannot = permission => {
    return !can(permission);
  };

  /**
   * Check if user can perform any of the given permissions
   */
  const canAny = permissions => {
    return permissions.some(permission => can(permission));
  };

  /**
   * Check if user can perform all of the given permissions
   */
  const canAll = permissions => {
    return permissions.every(permission => can(permission));
  };

  /**
   * Check if user has specific role
   */
  const isRole = role => {
    return hasRole(role);
  };

  /**
   * Check if user has any of the given roles
   */
  const isAnyRole = roles => {
    return roles.some(role => hasRole(role));
  };

  /**
   * Get user's role
   */
  const getUserRole = () => {
    return user?.role || null;
  };

  /**
   * Check if user is admin
   */
  const isAdmin = () => {
    return isRole(ROLES.ADMIN);
  };

  /**
   * Check if user is manager or above
   */
  const isManagerOrAbove = () => {
    return isAnyRole([ROLES.ADMIN, ROLES.MANAGER]);
  };

  /**
   * Get permissions for a specific module
   */
  const getModulePermissions = module => {
    const modulePrefix = `${module.toLowerCase()}:`;
    return userPermissions.filter(permission =>
      permission.startsWith(modulePrefix)
    );
  };

  /**
   * Check if user can access module
   */
  const canAccessModule = module => {
    const viewPermission = `${module.toLowerCase()}:view`;
    return can(viewPermission);
  };

  /**
   * Check CRUD permissions for a module
   */
  const getModuleCRUDPermissions = module => {
    const modulePrefix = module.toLowerCase();
    return {
      canView: can(`${modulePrefix}:view`),
      canCreate: can(`${modulePrefix}:create`),
      canEdit: can(`${modulePrefix}:edit`),
      canDelete: can(`${modulePrefix}:delete`),
    };
  };

  /**
   * Filter items based on permissions
   */
  const filterByPermission = (items, getPermission) => {
    return items.filter(item => {
      const permission =
        typeof getPermission === 'function'
          ? getPermission(item)
          : getPermission;
      return can(permission);
    });
  };

  /**
   * Get allowed actions for current user
   */
  const getAllowedActions = actions => {
    return actions.filter(action => {
      if (action.permission) {
        return can(action.permission);
      }
      if (action.role) {
        return isRole(action.role);
      }
      return true;
    });
  };

  return {
    // Permission checking
    can,
    cannot,
    canAny,
    canAll,

    // Role checking
    isRole,
    isAnyRole,
    getUserRole,
    isAdmin,
    isManagerOrAbove,

    // Module permissions
    getModulePermissions,
    canAccessModule,
    getModuleCRUDPermissions,

    // Utilities
    filterByPermission,
    getAllowedActions,

    // Data
    userPermissions,
    user,
  };
};
